<template>
  <form-item
    v-if="isForm"
    :name="name"
    :prop="prop"
    :rules="rules"
    :hasLabel="hasLabel"
    :label="label"
    :hasDesc="hasDesc"
    :descContent="descContent"
    >
    <el-select
      v-show="!isShowSort"
      v-model="val"
      v-bind="$attrs"
      @focus="onFocus"
      @input="onInput"
      @remove-tag="onRemoveTag"
      @keydown.native.enter.capture="onKeyEnter"
      class="form-item"
      :loading="loading"
      :remote="inputStore === ''"
      :remote-method="getList"
      :value-key="keyName"
      :placeholder="placeholder"
      filterable
      reserve-keyword
      ref="select"
    >
      <div v-if="groupBy">
        <el-option-group
          v-for="g in groups"
          :key="g.key"
          :label="g.label"
        >
          <el-option
            v-for="(item, i) in g.options"
            :key="`${i}-${item[keyName]}`"
            :label="getLabel(item)"
            :value="item[valueName]"
            :disabled="getDisabled(item)"
          >
            <span>{{ getDropdownLabel(item) }}</span>
          </el-option>
        </el-option-group>
      </div>
      <div v-else>
        <el-option
          v-for="(item, i) in opts"
          :key="`${i}-${item[keyName]}`"
          :label="getLabel(item)"
          :value="item[valueName]"
          :disabled="getDisabled(item)"
        >
          <span>{{ getDropdownLabel(item) }}</span>
        </el-option>
      </div>
    </el-select>
    <div v-if="isShowSort">
      <draggable
        v-model="val"
        element="div"
        @change="onInput"
        ref="draggable"
      >
        <el-tag
          class="sort"
          v-for="v in val"
          :key="v"
          :disable-transitions="true"
          :type="getTagType(v)"
          >
          {{ getOptLabel(v) }}
        </el-tag>
      </draggable>
      <el-alert
        :title="$t('api.sortOptionsDesc')"
        type="info"
        >
      </el-alert>
    </div>
    <el-button
      v-if="isAllowSort && val && val.length > 1"
      type="primary"
      size="mini"
      @click="toggleSort"
      >
      <span v-if="isShowSort">
        {{ $t('api.sortOptionsDone') }}
      </span>
      <span v-else>
        {{ $t('api.sortOptions') }}
      </span>
    </el-button>
    <product-selector
      v-if="selector === 'product' && !$attrs.disabled"
      v-model="val"
      :base="base"
      :name="name"
      :opts="opts"
      :optsMap="optsMap"
      :filterObj="filterObj"
    >
    </product-selector>
  </form-item>
  <el-table-column
    v-else
    :label="label"
    align="center"
    :prop="dataName"
    :column-key="dataName"
    :filters="filters"
    :filtered-value="filteredValue"
    :filter-multiple="isFilterMulti"
    :width="$attrs.width"
    :min-width="$attrs.minWidth"
  >
    <template slot-scope="scope">
      <div
        v-if="Array.isArray(scope.row[name])"
      >
        <item-link
          v-for="d in scope.row[name]"
          :key="d[keyName]"
          displayType="tag"
          :base="name"
          basePath="/region/region"
          :idField="data"
          :nameField="labelName"
          :customLabel="getLabel(d)"
          :rowData="d"
          :fields="$attrs.fields"
          effect="plain"
          type="info"
          size="medium"
          :disable-transitions="true"
        >
          <template slot="prepend">
            <slot name="prepend" :row="d"></slot>
          </template>
        </item-link>
      </div>
      <div
        v-else
      >
          {{ getListLabel(scope.row) }}
      </div>
    </template>
  </el-table-column>
</template>

<script>

import draggable from 'vuedraggable'
import { getTransText } from '@/utils/i18n'
import { updateFilter } from '@/utils/filter'

export default {
  components: {
    draggable
  },
  name: 'remote-opt',
  props: {
    value: null,
    base: {
      type: String,
      default: ''
    },
    isForm: {
      type: Boolean,
      default: true
    },
    name: {
      type: String,
      required: true
    },
    prop: {
      type: String,
      default: ''
    },
    data: {
      type: String,
      default: ''
    },
    keyName: {
      type: String,
      default: 'id'
    },
    listLabel: {
      type: String,
      default: ''
    },
    labelName: {
      type: String,
      default: 'name'
    },
    labelArr: {
      type: Array,
      default: () => []
    },
    filterLabelArr: {
      type: Array,
      default: () => []
    },
    dropLabelArr: {
      type: Array,
      default: undefined
    },
    valueName: {
      type: String,
      default: 'id'
    },
    fetchList: {
      type: Function,
      default: null
    },
    sort: {
      type: String,
      default: 'name'
    },
    pageLimit: {
      type: Number,
      default: 100
    },
    baseFilter: {
      type: String,
      default: ''
    },
    srchFilter: {
      type: String,
      default: '{name:"%`value`%"}'
    },
    filterObj: {
      type: Object,
      default: () => {}
    },
    required: {
      type: Boolean,
      default: false
    },
    searchDelay: {
      type: Number,
      default: 300
    },
    labelText: {
      type: String,
      default: ''
    },
    inputList: {
      type: Array,
      default: () => []
    },
    inputStore: {
      type: String,
      default: ''
    },
    getDisabled: {
      type: Function,
      default: () => {
        return false
      }
    },
    isAutoFetch: {
      type: Boolean,
      default: true
    },
    isAllowSort: {
      type: Boolean,
      default: false
    },
    isNeedQuery: {
      type: Boolean,
      default: false
    },
    isNeedReset: {
      type: Boolean,
      default: false
    },
    isTagStyle: {
      type: Boolean,
      default: false
    },
    isFilterAllowMulti: {
      type: Boolean,
      default: true
    },
    hasLabel: {
      type: Boolean,
      default: true
    },
    hasDesc: {
      type: Boolean,
      default: false
    },
    selector: {
      type: String,
      default: ''
    },
    groupBy: {
      type: String,
      default: ''
    },
    groupLabel: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      val: this.value,
      prevVal: this.value,
      loading: false,
      timer: null,
      dataName: this.data || this.name,
      isInited: false,
      isShowSort: false,
      opts: [],
      groups: [],
      optsMap: {},
      listQuery: {
        sort: this.sort,
        page: 1,
        limit: this.pageLimit
      },
      filteredValue: []
    }
  },
  computed: {
    isFilterMulti () {
      if (!this.isFilterAllowMulti) {
        return false
      }
      return this.opts.length > 2
    },
    label () {
      return this.labelText || this.getTransText(this.name, 'label', this.base)
    },
    placeholder () {
      return this.getTransText(this.name, 'placeholder', this.base) ||
        this.getTransText(this.name, 'remoteHolder', this.base)
    },
    descContent () {
      return this.getTransText(this.name, 'desc', this.base)
    },
    rules () {
      return (this.required) ? [{
        required: true,
        validator: (rule, value, callback) => {
          if (this.val === null ||
            typeof this.val === 'undefined' ||
            this.val.length <= 0) {
            callback(new Error(this.getTransText(this.name, 'error', this.base)))
          } else {
            callback()
          }
        },
        trigger: 'blur'
      }] : this.$attrs.rules
    },
    filters () {
      let filters = []
      for (let i = 0; i < this.opts.length; i++) {
        let opt = this.opts[i]
        filters[i] = {
          value: opt[this.valueName],
          text: this.getLabel(opt, this.filterLabelArr)
        }
      }
      return filters
    }
  },
  mounted () {
    if (this.baseFilter !== '') {
      this.listQuery.filter = this.baseFilter
    }

    this.updOpts(this.inputList)
    this.loadStore()
    this.$ebus.$on('renewLang', this.renewLang)

    if (this.isForm) {
      if (this.isAutoFetch) {
        this.fetch()
        this.isInited = true
      }
    } else {
      this.$nextTick(() => {
        this.updateFilter(this.dataName)
      })
    }
    if (this.isTagStyle) {
      this.$watch(
        () => {
          return this.$refs.select.inputHovering
        },
        (val) => {
          this.updateTagStyle()
        }
      )
    }
  },
  beforeDestroy () {
    this.$ebus.$off('renewLang', this.renewLang)
  },
  updated () {
    this.updateTagStyle()
  },
  methods: {
    updateFilter,
    getTransText,
    onKeyEnter (e) {
      // Prevent trigger select action when pressing enter for Chinese input
      if (this.$refs.select.isOnComposition) {
        e.preventDefault()
        e.stopPropagation()
      }
    },
    renewLang () {
      this.loadStore()
      if (this.isForm) {
        this.fetch()
        this.$nextTick(() => {
          // Prevent displaying placeholder after language changed
          let s = this.$refs.select
          if (s.selected.length > 0) {
            s.currentPlaceholder = ''
          }
        })
      }
    },
    updateTagStyle () {
      if (!this.isTagStyle ||
        this.$attrs.multiple === undefined ||
        this.val.length <= 0) {
        return
      }
      this.$nextTick(() => {
        // Add CSS class to tag node directly because no settings available for el-select
        let tags = this.$refs.select.$refs.tags.childNodes[1].childNodes
        if (tags.length === this.val.length) {
          for (let i = 0; i < tags.length; ++i) {
            let tag = tags[i]
            let v = this.val[i]
            if (v) {
              tag.classList.add('el-tag--' + this.getTagType(v))
            }
          }
        }
      })
    },
    loadStore () {
      if (!this.inputStore) {
        return
      }
      this.$store.dispatch('Init' + this.inputStore, this.$t)
        .then((data) => {
          if (data) {
            this.opts = data.opts
            this.optsMap = data.optsMap
          }
        })
    },
    reset () {
      this.listQuery.filter = this.baseFilter
      this.opts.splice(0, this.opts.length)
      this.optsMap = {}
    },
    getTagType (v, t) {
      let opt = this.optsMap[v]
      if (opt && opt.status === 0) {
        if (opt.end_at && this.$moment().isAfter(this.$moment(opt.end_at))) {
          return t ? t('api.optionStatus.expired') : 'warning'
        } else {
          return t ? t('api.optionStatus.show') : 'success'
        }
      }
      return t ? t('api.optionStatus.hide') : 'info'
    },
    toggleSort () {
      this.isShowSort = !this.isShowSort
    },
    getOptLabel (val) {
      let opt = this.optsMap[val]
      if (typeof opt !== 'undefined') {
        return this.getLabel(opt)
      }
      return val
    },
    getDropdownLabel (item) {
      let s = this.isTagStyle ? ' [' + this.getTagType(item[this.valueName], this.$t) + ']' : ''
      return this.getLabel(item, this.dropLabelArr) + s
    },
    getListLabel (item) {
      if (this.listLabel) {
        return item[this.listLabel]
      }
      return this.getOptLabel(item[this.dataName])
    },
    getLabel (item, arr) {
      arr = (arr !== undefined) ? arr : this.labelArr
      if (arr && arr.length > 0) {
        let label = ''
        for (let i = 0; i < arr.length; ++i) {
          let lbl = arr[i]
          if (lbl !== undefined && lbl !== null) {
            let n = null
            if (lbl.label && lbl.sub) {
              let l = item[lbl.label] || '-'
              let sub = item[lbl.sub] || '-'
              n = l + ' (' + sub + ')'
            } else {
              n = item[lbl]
            }
            if (n !== undefined && n !== null) {
              label += n
              if (i < arr.length - 1) {
                label += ' - '
              }
            }
          }
        }
        return label
      }
      return item[this.labelName]
    },
    getList (query) {
      if (query !== '') {
        let intQ = parseInt(query, 10) || 0
        let f = this.srchFilter.replace(/`value`/g, query)
        f = f.replace(/`int`/g, intQ)
        this.listQuery.filter = f
      } else {
        this.listQuery.filter = this.baseFilter
      }
      this.updList()
    },
    updList () {
      if (this.searchDelay) {
        clearTimeout(this.timer)
        this.timer = setTimeout(() => {
          this.fetch()
        }, this.searchDelay)
      } else {
        this.fetch()
      }
    },
    updOpts (list) {
      if (!list) {
        return
      }
      let opts = []
      for (let i = 0; i < list.length; ++i) {
        let opt = list[i]
        if (opt) {
          let key = opt[this.keyName]
          if (typeof this.optsMap[key] === 'undefined') {
            opts.push(opt)
          }
          this.optsMap[key] = opt
        }
      }
      this.opts = opts.concat(this.opts)
    },
    resetOpts () {
      let optsMap = {}
      let opts = []
      if (this.val) {
        for (let i = 0; i < this.val.length; ++i) {
          let key = this.val[i]
          if (typeof this.optsMap[key] !== 'undefined') {
            let opt = Object.assign({}, this.optsMap[key])
            optsMap[key] = opt
            opts.push(opt)
          }
        }
      }
      this.optsMap = optsMap
      this.opts = opts
    },
    fetch () {
      if (typeof this.fetchList !== 'function') {
        return
      }
      if (this.isNeedQuery) {
        if (this.listQuery.filter === undefined ||
          this.listQuery.filter === '') {
          this.reset()
          return
        }
      }
      this.loading = true
      this.fetchList(this.listQuery).then(res => {
        if (res && res.data) {
          this.resetOpts()
          this.updOpts(res.data)
          this.$emit('fetch', this.optsMap)
        }
      }).catch(error => {
        console.log(error)
      }).then(() => {
        this.loading = false
      })
    },
    onFocus () {
      if (!this.isAutoFetch && !this.isInited) {
        this.fetch()
        this.isInited = true
      }
    },
    onInput () {
      this.$emit('input', this.val)
      this.$emit('change', this.val, this.optsMap[this.val])
    },
    onRemoveTag (v) {
      this.$emit('remove-tag', v, this.prevVal)
    },
    genGroupOpts (opts) {
      let g = {}
      let groups = []
      let idx = 0
      opts.forEach((opt) => {
        let gid = opt[this.groupBy]
        if (gid !== undefined) {
          if (g[gid] === undefined) {
            groups.push({
              key: gid,
              label: opt[this.groupLabel] || '-',
              options: [ opt ]
            })
            g[gid] = idx++
          } else {
            groups[g[gid]].options.push(opt)
          }
        }
      })
      this.groups = groups
    }
  },
  watch: {
    opts (v) {
      if (this.groupBy) {
        this.genGroupOpts(v)
      }
    },
    baseFilter () {
      this.listQuery.filter = this.baseFilter
      if (this.isAutoFetch) {
        this.fetch()
      } else {
        if (this.isNeedReset) {
          this.isInited = false
        }
      }
    },
    inputList (v) {
      this.updOpts(v)
    },
    value (v) {
      this.prevVal = this.val
      this.val = v
    },
    '$route.query' () {
      this.updateFilter(this.dataName)
    }
  }
}
</script>
